import { promotionWaitSummary, type PromotionWaitSummary } from '@term-deck/core/promotion-wait.js';
import { create } from 'zustand';
import type { CloseManyRequest, CloseManyView } from '../../../core/src/close-many-preview';
import type {
  CodexEffortLevel,
  CodexModelId,
  EffortLevel,
  ModelId,
  PauseReason,
  ProjectEntry,
  Provider,
  SessionInfo,
} from '@term-deck/core/types';
import { api, type HealthInfo } from '../api/client';
import type { ConfirmRequest } from '../components/ConfirmModal';
import { friendlyError } from '../lib/friendly-error';

/**
 * M445.13 — o conflito de single-flight tem codigo proprio (`project_execution_in_flight`) e tambem aparece
 * na mensagem; casar os dois evita depender de um formato so. O single-flight e LEGITIMO (impede duas
 * sessoes vivas no mesmo projeto) — o defeito era apresenta-lo ao dono como falha de escrita.
 */
function isExecutionInFlight(err: unknown): boolean {
  const code = (err as { reasonCode?: unknown } | null)?.reasonCode;
  if (typeof code === 'string' && code === 'project_execution_in_flight') return true;
  return /project_execution_in_flight/i.test(err instanceof Error ? err.message : String(err ?? ''));
}

/** An explicit destination never reuses a conversation observed on another device. */
function conversationSessions(sessions: SessionInfo[], deviceId?: string): SessionInfo[] {
  return sessions.filter(s => !s.tema && (deviceId === undefined || s.deviceId === deviceId));
}

interface DeckState {
  sessions: SessionInfo[];
  projects: ProjectEntry[];
  /** Sessao em foco no painel principal. */
  focusedId: string | null;
  /**
   * M448 — o foco atual veio de uma ESCOLHA do usuário, ou de reatribuição automática?
   *
   * Existe porque o App usa a mudança de `focusedId` para, no celular, pular ao painel do terminal
   * (M8-WEB#27: quem lança um projeto pela lista não pode ficar preso na lista). Só que o `refresh()`
   * também reatribui o foco sozinho — na primeira carga e sempre que a sessão focada morre — e aí o mesmo
   * efeito arrastava o usuário para o terminal SEM ele ter pedido nada. Medido na varredura: tocar
   * "Projetos" logo depois de abrir o app devolvia `data-mobile-panel="terminal"` em segundos.
   */
  focoAutomatico: boolean;
  bootstrapAvailable: boolean;
  loading: boolean;
  error: string | null;
  /** Pedido de confirmacao destrutiva ativo (M5.6) ou null. */
  confirm: ConfirmRequest | null;
  /** Estado da conexao SSE da frota (M8-WEB#30): 'live' | 'reconnecting'. */
  fleetConnection: 'live' | 'reconnecting';
  /**
   * Estado da conexao WS do TERMINAL EM FOCO (M17.4.2): 'live' | 'reconnecting'. Alimentado pelo
   * TerminalView da sessao focada para exibir um banner honesto "servidor reiniciando, reconectando…"
   * (em vez do "erro de conexao" cru que levou o usuario a recarregar — e perder o audio). Some ao
   * reconectar. Como o painel principal foca 1 terminal por vez, 1 estado basta.
   */
  sessionConnection: 'live' | 'reconnecting';
  /** Atualiza o estado da conexao do terminal em foco (M17.4.2). */
  setSessionConnection: (state: 'live' | 'reconnecting') => void;
  /**
   * M15.1.4 — quantas sessoes foram reabertas no ultimo boot do server (auto-reabertura Tier A).
   * 0 = nenhuma (boot normal). > 0 dispara o banner "N sessoes reabertas apos reinicio".
   */
  restoredOnBoot: number;
  /**
   * M18.1.1 — bootId do server a que o `restoredOnBoot` corrente pertence. Usado para reconhecer o
   * banner por BOOT (persistido em localStorage), nao por sessao de memoria — assim ele aparece 1x
   * por boot REAL por dispositivo e sobrevive a refresh/reabertura do PWA.
   */
  restoredBootId: string | null;
  /** Dispensa o banner de restauracao (M15.1.4 + M18.1.1: persiste o bootId reconhecido). */
  dismissRestoredBanner: () => void;
  /**
   * FASE 42 — quando o disco tem uma versao mergeada AGUARDANDO restart (cutover segurado por sessao
   * viva). null = em dia. Alimenta o pill LEVE do header ("vN aguardando restart"); pura observabilidade.
   */
  pendingRestart: { runningVersion: string; diskVersion: string; wait: PromotionWaitSummary | null } | null;
  /**
   * FASE 179 (179.4) — circuit-breaker de BUILD ativo (deploy TRAVADO: [dirty]/[live-session]/build-fail).
   * null = deploy fluindo. Alimenta o chip do header (some sozinho quando o /api/health volta null).
   */
  deployBlocked: DeployBlockedUi | null;

  refreshSessions: () => Promise<void>;
  refreshProjects: () => Promise<void>;
  refreshHealth: () => Promise<void>;
  /**
   * M18.5/M18.10 — cria um projeto novo (Pessoal/Workana/Geral), refaz a lista e retorna o
   * ProjectEntry criado (ou null em erro, que ja seta `error`). A UI usa o retorno p/ oferecer abrir.
   */
  createProject: (
    name: string,
    category: 'Pessoal' | 'Workana' | 'Geral',
  ) => Promise<ProjectEntry | null>;
  openSession: (params: {
    provider: Provider;
    cwd: string;
    deviceId?: string;
    category?: string;
    projectName?: string;
    /** FASE 30 — override de effort do seletor (so claude; o server decide/ignora se N/A). */
    effort?: EffortLevel;
    /** FASE 59 — override de modelo do seletor (so claude; o server decide/ignora se N/A). */
    model?: ModelId;
    /** FASE 150 (150.D — promove 149.1.4b) — override de modelo/effort Codex do seletor (so codex). */
    codexModel?: CodexModelId;
    codexEffort?: CodexEffortLevel;
    grokModel?: string;
    grokEffort?: 'none' | 'minimal' | 'low' | 'medium' | 'high' | 'xhigh' | 'max';
    /** FASE 117 (117.B.2) — abrir JA como manual (isolado dos motores), opt-out antes do spawn. */
    openAsManual?: boolean;
  }) => Promise<SessionInfo | null>;
  /**
   * M19.3 — abre N projetos em LOTE ad-hoc (selecao na hora). Nao reabre quem ja tem sessao viva
   * (skipped); coleta falhas parciais (teto de PTYs) sem abortar. Refaz a lista e foca a primeira
   * aberta. Retorna o resumo (launched/skipped/failed) para a UI dar o aviso de resultado parcial.
   */
  openMany: (
    items: Array<{ provider: Provider; cwd: string; category?: string; projectName?: string }>,
  ) => Promise<{ launched: number; skipped: number; failed: number } | null>;
  closeSession: (id: string) => Promise<void>;
  closeMany: (filter: CloseManyRequest) => Promise<CloseManyView>;
  /**
   * FASE 23 (M23.A.5) — pausa propositalmente um terminal (snooze). Atualiza o snapshot na hora (o SSE
   * `status` tambem chega). Seta erro em falha.
   */
  pauseSession: (
    id: string,
    opts: { reason: PauseReason; resumeAtMs?: number | null; reopen?: boolean },
  ) => Promise<void>;
  /** FASE 23 (M23.A.7) — retoma (despausa/antecipa) um terminal pausado. */
  unpauseSession: (id: string) => Promise<void>;
  /** Reinicia uma sessao (M5.3): /exit + relancar --continue no mesmo cwd. */
  restartSession: (id: string) => Promise<void>;
  /** ECO.10 — o servidor anunciou o relé do terminal pronto (`/api/health`). */
  terminalRelayReady: boolean;
  /** ECO.8 — leva a sessão para uma aba do Windows Terminal no PC (a mesma sessão; a web continua funcionando). */
  openNativeWindow: (id: string) => Promise<boolean>;
  setFocused: (id: string | null) => void;
  /** Define (ou limpa) a barra de erro/aviso global (M9.5: aviso de clipboard negado). */
  setError: (message: string | null) => void;
  /** Atualiza/insere o snapshot de uma sessao (vindo de evento WS). */
  upsertSession: (info: SessionInfo) => void;
  /** Remove uma sessao da lista (auto-reap pos-exit, M5.2). */
  removeSession: (id: string) => void;
  /** Abre o modal de confirmacao destrutiva (M5.6). */
  requestConfirm: (req: ConfirmRequest) => void;
  /** Fecha o modal de confirmacao. */
  dismissConfirm: () => void;
  /** Assina o SSE da frota (/api/events) p/ reatividade imediata. Retorna o unsubscribe. */
  subscribeFleetEvents: () => () => void;
  /**
   * Abre um projeto pela sidebar com a regra do M5.7: se ja ha um terminal VIVO para o mesmo
   * cwd, NAO duplica — se for o MESMO provider, so foca o existente; se for OUTRO provider,
   * pede confirmacao (via requestConfirm) e, ao confirmar, encerra o anterior e reabre.
   */
  openOrSwitchProject: (params: {
    provider: Provider;
    cwd: string;
    deviceId?: string;
    category: string;
    projectName: string;
    /** FASE 30 — override de effort do seletor (so claude; o server decide/ignora se N/A). */
    effort?: EffortLevel;
    /** FASE 59 — override de modelo do seletor (so claude; o server decide/ignora se N/A). */
    model?: ModelId;
    /** FASE 150 (150.D — promove 149.1.4b) — override de modelo/effort Codex do seletor (so codex). */
    codexModel?: CodexModelId;
    codexEffort?: CodexEffortLevel;
    /** FASE 117 (117.B.2) — abrir JA como manual (isolado dos motores), opt-out antes do spawn. */
    openAsManual?: boolean;
  }) => Promise<void>;
}

export const useDeckStore = create<DeckState>((set, get) => ({
  sessions: [],
  projects: [],
  focusedId: null,
  focoAutomatico: true,
  bootstrapAvailable: true,
  loading: false,
  error: null,
  terminalRelayReady: false,
  confirm: null,
  fleetConnection: 'live',
  sessionConnection: 'live',
  restoredOnBoot: 0,
  restoredBootId: null,
  pendingRestart: null,
  deployBlocked: null,

  setSessionConnection: (state) =>
    set((s) => (s.sessionConnection === state ? s : { sessionConnection: state })),

  dismissRestoredBanner: () =>
    set((s) => {
      // M18.1.1 — persiste o bootId reconhecido para NAO re-anunciar este mesmo boot em refreshes
      // futuros / reabertura do PWA. Um boot REAL (novo bootId) volta a disparar o banner.
      if (s.restoredBootId) setSeenBootId(s.restoredBootId);
      return { restoredOnBoot: 0 };
    }),

  refreshSessions: async () => {
    try {
      const sessions = await api.listSessions();
      set({ sessions });
      // O foco do dashboard vai SEMPRE para um terminal vivo (nunca o orquestrador, que vive
      // em /chat — M5.8; nunca uma sessao exited — M5.2).
      const focusable = sessions.filter((s) => s.kind !== 'orchestrator' && s.status !== 'exited');
      const { focusedId } = get();
      const focusStillValid = focusedId !== null && focusable.some((s) => s.id === focusedId);
      if (!focusStillValid) {
        set({ focusedId: focusable[0]?.id ?? null, focoAutomatico: true });
      }
    } catch (err) {
      set({ error: errMsg(err) });
    }
  },

  refreshProjects: async () => {
    try {
      const projects = await api.listProjects();
      set({ projects });
    } catch (err) {
      set({ error: errMsg(err) });
    }
  },

  createProject: async (name, category) => {
    try {
      const project = await api.createProject(name, category);
      await get().refreshProjects();
      return project;
    } catch (err) {
      set({ error: errMsg(err) });
      return null;
    }
  },

  refreshHealth: async () => {
    try {
      const h = await api.health();
      // M18.1.1 — o banner so aparece quando o bootId do server NAO foi reconhecido ainda neste
      // dispositivo (localStorage). Antes, `restoredBannerSeen` vivia so em memoria e o banner
      // re-anunciava um boot ANTIGO a cada refresh/reabertura do PWA (bug confirmado).
      const bootId = h.bootId ?? null;
      const restored = h.restoredOnBoot ?? 0;
      const alreadySeen = bootId !== null && seenBootId() === bootId;
      const shouldShow = restored > 0 && bootId !== null && !alreadySeen;
      set({
        bootstrapAvailable: h.bootstrapAvailable,
        // FASE 42 — pill leve "vN aguardando restart" (null quando em dia).
        pendingRestart: selectPendingRestart(h),
        // FASE 179 (179.4) — chip "deploy bloqueado" (null quando o circuit-breaker limpa).
        deployBlocked: selectDeployBlocked(h),
        terminalRelayReady: h.terminalRelay?.state === 'ready',
        ...(shouldShow ? { restoredOnBoot: restored, restoredBootId: bootId } : {}),
      });
    } catch (err) {
      set({ error: errMsg(err) });
    }
  },

  openSession: async (params) => {
    set({ loading: true, error: null });
    try {
      const info = await api.createSession(params);
      // UPSERT-by-id idempotente (M8-WEB#28 / M7.9): o SSE `created` da nova sessao pode chegar
      // ANTES desta resposta (corrida POST-vs-SSE). Append cego duplicaria o tile (keys React
      // repetidas -> tile-fantasma). `mergeSession` insere OU atualiza por id — nunca duplica.
      set((s) => ({
        sessions: mergeSession(s.sessions, info),
        focusedId: info.id,
        loading: false,
      }));
      return info;
    } catch (err) {
      // M445.13 — `project_execution_in_flight` NAO e erro para quem clicou: e "voce ja tem esse terminal
      // aberto". O `openOrSwitchProject` ja troca para o existente, mas decide pela lista do CLIENTE — e ela
      // pode estar velha (caso comum: um MOTOR abriu o terminal e o navegador ainda nao viu). Nesse caminho o
      // clique caia num 409 cru. Aqui a lista e RECARREGADA da fonte e, achando a sessao viva do mesmo cwd,
      // focamos nela. Sem sessao viva, o erro segue: conflito sem dono visivel continua sendo erro de verdade.
      if (isExecutionInFlight(err)) {
        try {
          const frescas = await api.listSessions();
          const viva = liveSessionForCwd(conversationSessions(frescas, params.deviceId).filter(s => s.provider === params.provider), params.cwd);
          if (viva) {
            set({ sessions: frescas, focusedId: viva.id, loading: false, error: null });
            return viva;
          }
          set({ sessions: frescas });
        } catch {
          /* releitura best-effort: falhando, cai no erro original abaixo */
        }
      }
      set({ error: errMsg(err), loading: false });
      return null;
    }
  },

  openMany: async (items) => {
    if (items.length === 0) return { launched: 0, skipped: 0, failed: 0 };
    set({ loading: true, error: null });
    try {
      const r = await api.launchMany(items);
      // O SSE `created` ja insere cada nova sessao; um refresh garante consistencia + foca a 1a aberta.
      await get().refreshSessions();
      const firstId = r.launched[0]?.id;
      if (firstId) set({ focusedId: firstId });
      if (r.failed.length > 0) {
        set({
          error: `Lote: ${r.launched.length} aberto(s), ${r.skipped.length} ja aberto(s), ${r.failed.length} falhou (teto de PTYs?)`,
        });
      }
      set({ loading: false });
      return { launched: r.launched.length, skipped: r.skipped.length, failed: r.failed.length };
    } catch (err) {
      set({ error: errMsg(err), loading: false });
      return null;
    }
  },

  closeSession: async (id) => {
    try {
      await api.closeSession(id);
      // Mantem a sessao na lista marcada como exited (evento WS atualiza), mas
      // forca refresh para refletir o status.
      await get().refreshSessions();
    } catch (err) {
      set({ error: errMsg(err) });
    }
  },

  closeMany: async (filter) => {
    try {
      const result = await api.closeMany(filter);
      await get().refreshSessions();
      return result;
    } catch (err) {
      set({ error: errMsg(err) });
      throw err;
    }
  },

  pauseSession: async (id, opts) => {
    try {
      const info = await api.pauseSession(id, opts);
      set((s) => ({ sessions: mergeSession(s.sessions, info) }));
    } catch (err) {
      set({ error: errMsg(err) });
    }
  },

  unpauseSession: async (id) => {
    try {
      const info = await api.unpauseSession(id);
      if (info) set((s) => ({ sessions: mergeSession(s.sessions, info) }));
      else await get().refreshSessions();
    } catch (err) {
      set({ error: errMsg(err) });
    }
  },

  openNativeWindow: async (id) => {
    try {
      const r = await api.openNativeWindow(id);
      if (!r.launched) {
        set({ error: r.reason === 'td_client_missing'
          ? 'Não deu para abrir a janela: o comando td ainda não está instalado neste PC.'
          : r.reason === 'platform_unsupported'
            ? 'Janela nativa só existe no PC com Windows onde o term-deck roda.'
            : `Não deu para abrir a janela (${r.reason ?? 'motivo desconhecido'}).` });
      }
      return r.launched;
    } catch (err) {
      set({ error: errMsg(err) });
      return false;
    }
  },

  restartSession: async (id) => {
    try {
      const info = await api.restartSession(id);
      // A sessao antiga sera auto-reaped (evento `removed`); a nova entra via UPSERT idempotente
      // (M7.9 / M8-WEB#28): o filtro do id antigo remove o predecessor; `mergeSession` evita
      // duplicar a NOVA caso o SSE `created` dela ja tenha inserido (corrida).
      set((s) => ({
        sessions: mergeSession(
          s.sessions.filter((x) => x.id !== id),
          info,
        ),
        focusedId: info.id,
      }));
      await get().refreshSessions();
    } catch (err) {
      set({ error: errMsg(err) });
    }
  },

  setFocused: (id) => set({ focusedId: id, focoAutomatico: false }),

  setError: (message) => set({ error: message }),

  requestConfirm: (req) => set({ confirm: req }),
  dismissConfirm: () => set({ confirm: null }),

  openOrSwitchProject: async (params) => {
    const { sessions, projects, openSession, closeSession, setFocused, requestConfirm } = get();

    // FASE 23 (M23.B.3) — guard anti-retomada cross-provider: se o cwd tem trabalho preso ao CODEX e o
    // provider alvo NAO e codex, ALERTA + pede confirmacao explicita (continuidade do codex so vale no
    // codex; abrir outro provider comeca do zero). Vale antes de qualquer abertura (haja sessao ou nao).
    const entry = projects.find((p) => normalizeCwd(p.cwd) === normalizeCwd(params.cwd));
    if (entry?.codexLocked && params.provider !== 'codex') {
      requestConfirm({
        title: 'Trabalho iniciado no codex',
        message: `"${params.projectName}" tem trabalho iniciado no codex — a continuidade so vale no codex (os demais providers usam outro harness). Abrir com ${params.provider} comeca do ZERO. Continuar mesmo assim?`,
        confirmLabel: `Abrir com ${params.provider} (do zero)`,
        tone: 'danger',
        onConfirm: async () => {
          const ex = liveSessionForCwd(conversationSessions(get().sessions, params.deviceId), params.cwd);
          if (ex && ex.provider !== params.provider) await closeSession(ex.id);
          // FASE 28 (28.A.1) — o usuario CONFIRMOU abrir outro provider num cwd codex-locked. Limpa o
          // lock codex (e grava a pref do novo provider) via setProjectPref com force ANTES de abrir;
          // sem isso o lock persiste e o aviso ENGANOSO de reset reaparece na proxima abertura,
          // induzindo o usuario a resetar uma sessao com trabalho. Best-effort: a abertura segue mesmo
          // se a limpeza do lock falhar (ex: cwd nao rastreavel pelo restore).
          try {
            if (params.deviceId === undefined) await api.setProjectPref(params.cwd, params.provider, { force: true });
          } catch {
            /* best-effort */
          }
          await openSession(params);
          // Atualiza os cards para refletir codexLocked=false + a nova pref imediatamente.
          void get().refreshProjects();
        },
      });
      return;
    }

    const existing = liveSessionForCwd(conversationSessions(sessions, params.deviceId), params.cwd);

    if (!existing) {
      await openSession(params);
      return;
    }
    if (existing.provider === params.provider) {
      // Mesmo provider ja aberto: nao duplica — so foca o existente.
      setFocused(existing.id);
      return;
    }
    // Outro provider para o mesmo projeto: confirma a troca (encerra o anterior e reabre).
    requestConfirm({
      title: 'Trocar provider do projeto',
      message: `"${params.projectName}" ja tem um terminal ${existing.provider} aberto. Trocar para ${params.provider} vai ENCERRAR o terminal atual e reabrir com o novo provider. Continuar?`,
      confirmLabel: `Reabrir com ${params.provider}`,
      tone: 'danger',
      onConfirm: async () => {
        await closeSession(existing.id);
        await openSession(params);
      },
    });
  },

  upsertSession: (info) => set((s) => ({ sessions: mergeSession(s.sessions, info) })),

  removeSession: (id) =>
    set((s) => {
      const sessions = s.sessions.filter((x) => x.id !== id);
      const focusedId =
        s.focusedId === id
          ? (sessions.find((x) => x.status !== 'exited')?.id ?? null)
          : s.focusedId;
      return { sessions, focusedId };
    }),

  subscribeFleetEvents: () => {
    // SSE da frota (M2.5): reatividade imediata a created/status/exit/removed, sem esperar o
    // poll de 5s. O 'removed' (auto-reap pos-exit, M5.2) tira o tile-fantasma na hora.
    //
    // RESILIENCIA (M8-WEB#30): no celular (lock de tela / troca de rede) o SSE cai. O EventSource
    // nativo reconecta sozinho, mas (a) durante a queda a UI nao sabia (congelava em silencio) e
    // (b) ao voltar, eventos perdidos durante a queda nunca chegavam (volta o tile-fantasma). Aqui:
    // marcamos `reconnecting` no erro (pill na UI) e, ao reconectar (open), fazemos um RE-SYNC
    // completo via refreshSessions para recuperar o estado perdido. Backoff proprio com cap.
    let es: EventSource | null = null;
    let closed = false;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // FASE 115 (115.1) — refresh REATIVO dos PROJETOS. As flags derivadas (awaitingDecisionPersisted,
    // emptyGated, statusFlag, o badge "aberto") mudam server-side quando um terminal ABRE/FECHA, mas o
    // SSE da frota so atualiza SESSOES — os PROJETOS ficavam stale ate uma acao do usuario (era a causa
    // do filtro "aguardando você" mostrar 0 sobre dado velho). Debounce curto coalescs a rajada de
    // eventos (created + N status + exit + removed de um mesmo fechamento) num unico re-fetch.
    let projectsTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleProjectsRefresh = (): void => {
      if (closed || projectsTimer) return;
      projectsTimer = setTimeout(() => {
        projectsTimer = null;
        void get().refreshProjects();
      }, 1500);
    };

    const onCreatedOrStatus = (ev: MessageEvent): void => {
      try {
        const { info } = JSON.parse(ev.data) as { info?: SessionInfo };
        if (info) get().upsertSession(info);
      } catch (err) {
        // FASE 63.3 — payload malformado: nao falha mudo (era catch{} vazio). Descarta o evento, mas
        // deixa rastro no console (evento SSE corrompido do proprio server e sinal, ainda que raro).
        console.warn('[term-deck] evento SSE de sessao malformado, descartado:', err);
      }
    };
    // FASE 115 (115.1) — created/exit mexem no estado de PROJETO (abrir reseta o gate anti-vazios 115.2 e
    // liga o badge "aberto"; fechar liga awaitingDecisionPersisted / emptyGated). 'status' (mudanca de
    // atividade) NAO entra aqui — dispara com frequencia e nao altera as flags de projeto.
    const onLifecycle = (ev: MessageEvent): void => {
      onCreatedOrStatus(ev);
      scheduleProjectsRefresh();
    };
    const onRemoved = (ev: MessageEvent): void => {
      try {
        const { sessionId } = JSON.parse(ev.data) as { sessionId: string };
        if (sessionId) get().removeSession(sessionId);
      } catch (err) {
        console.warn('[term-deck] evento SSE de remocao malformado, descartado:', err); // FASE 63.3
      }
      scheduleProjectsRefresh(); // reap pos-exit: reflete o card sem "aberto" + flags novas na hora
    };

    const connect = (): void => {
      if (closed) return;
      es = new EventSource('/api/events', { withCredentials: true });
      es.addEventListener('open', () => {
        const wasReconnecting = get().fleetConnection === 'reconnecting';
        retry = 0;
        set({ fleetConnection: 'live' });
        // Re-sync ao (re)conectar: recupera eventos perdidos durante a queda.
        if (wasReconnecting) void get().refreshSessions();
      });
      // O snapshot inicial do SSE tambem ja traz o estado; tratamos via status.
      // FASE 115 (115.1) — created/exit tambem agendam um refresh dos PROJETOS (onLifecycle); 'status'
      // (mudanca de atividade, alta frequencia) so faz upsert da sessao (nao mexe nas flags de projeto).
      es.addEventListener('created', onLifecycle);
      es.addEventListener('status', onCreatedOrStatus);
      es.addEventListener('exit', onLifecycle);
      es.addEventListener('removed', onRemoved);
      es.onerror = (): void => {
        if (closed) return;
        set({ fleetConnection: 'reconnecting' });
        // Fecha e reconecta com backoff proprio (o auto-reconnect do EventSource pode ficar preso).
        es?.close();
        es = null;
        const delay = Math.min(15000, 1000 * 2 ** Math.min(retry, 4));
        retry += 1;
        timer = setTimeout(connect, delay);
      };
    };
    connect();

    return () => {
      closed = true;
      if (timer) clearTimeout(timer);
      if (projectsTimer) clearTimeout(projectsTimer); // FASE 115 (115.1)
      es?.close();
    };
  },
}));

/**
 * M19.2 — normaliza um cwd para comparacao robusta. BUG real exposto pelo badge "aberto": o
 * `ProjectEntry.cwd` (do scan) usa BACKSLASH no Windows (`C:\...`), mas o `SessionInfo.cwd` usa
 * FORWARD SLASH (`C:/...` — o portal spawna com forward slashes porque o ConPTY mangla backslash).
 * A comparacao `===` crua sempre falhava (badge nunca aparecia; e o "nao duplicar/focar" do
 * openOrSwitchProject ja estava silenciosamente quebrado para sessoes abertas pela UI). Normaliza
 * separadores para `/` + remove barra final + lowercase da LETRA DE DRIVE (Windows e case-insensitive
 * no drive). Nao mexe no resto do path (Windows preserva caixa nos nomes, mas e o mesmo path).
 */
export function normalizeCwd(cwd: string): string {
  const slashed = cwd.replace(/\\/g, '/').replace(/\/+$/, '');
  // lowercase so a letra de drive ("C:/..." -> "c:/...") para casar C:\ vs c:/.
  return slashed.replace(/^([a-zA-Z]):/, (_m, d: string) => `${d.toLowerCase()}:`);
}

/**
 * M19.2 — sessao VIVA (de terminal, nao orquestrador) para um cwd, ou undefined. "Viva" = nao
 * exited. Fonte unica do conceito "projeto ja aberto": usada pelo openOrSwitchProject (decidir
 * focar vs abrir), pelo badge "aberto" do card (M19.2.1) e pelo CTA "ir para" da fila (M19.2.2).
 * Compara cwds NORMALIZADOS (backslash vs forward slash — ver normalizeCwd). Exportada para teste.
 * Retorna a PRIMEIRA encontrada (o store nunca duplica cwd vivo).
 */
export function liveSessionForCwd(sessions: SessionInfo[], cwd: string): SessionInfo | undefined {
  const target = normalizeCwd(cwd);
  return sessions.find(
    (s) => s.kind !== 'orchestrator' && s.status !== 'exited' && normalizeCwd(s.cwd) === target,
  );
}

/** M19.2 — conveniencia booleana para a UI: ha sessao viva (terminal) para este cwd? */
export function isCwdOpen(sessions: SessionInfo[], cwd: string): boolean {
  return liveSessionForCwd(sessions, cwd) !== undefined;
}

/**
 * FASE 42 — deriva o estado do pill "aguardando restart" do payload do /api/health. Robusto a payloads
 * antigos (campos ausentes): so retorna o objeto quando pendingRestart=true E ambas as versoes vieram;
 * caso contrario null (sem pill). Pura — exportada para teste.
 */
export function selectPendingRestart(
  h: HealthInfo,
  nowMs = Date.now(),
): { runningVersion: string; diskVersion: string; wait: PromotionWaitSummary | null } | null {
  if (h.pendingRestart === true && h.runningVersion && h.diskVersion) {
    // ENT.7 — o motivo e há quanto tempo, em português (o keep-alive grava a categoria a cada tique).
    return { runningVersion: h.runningVersion, diskVersion: h.diskVersion, wait: promotionWaitSummary(h.promotionAdmission ?? null, nowMs) };
  }
  return null;
}

/** FASE 179 (179.4) — shape do chip "deploy bloqueado" derivado do /api/health. */
export interface DeployBlockedUi {
  version: string;
  reason: string;
  since: string | null;
  classification: string | null;
}

/**
 * FASE 179 (179.4) — deriva o chip "deploy bloqueado" do payload do /api/health. Robusto a payloads
 * antigos (campo ausente) e a shapes invalidos: so retorna o objeto quando `deployBlocked.version` veio;
 * caso contrario null (sem chip — o chip some sozinho quando o circuit-breaker limpa). Pura — testada.
 */
export function selectDeployBlocked(h: HealthInfo): DeployBlockedUi | null {
  const d = h.deployBlocked;
  if (!d || typeof d.version !== 'string' || d.version === '') return null;
  return {
    version: d.version,
    reason: typeof d.reason === 'string' ? d.reason : '',
    since: typeof d.since === 'string' ? d.since : null,
    classification: typeof d.classification === 'string' ? d.classification : null,
  };
}

/**
 * Insere OU atualiza um snapshot de sessao por id, sem duplicar (M8-WEB#28 / M7.9). Fonte unica de
 * verdade para openSession, restartSession, upsertSession e o merge do SSE — todos passam por aqui.
 * Exportada para teste de unidade (a corrida POST-vs-SSE que duplicava o tile e a regressao alvo).
 */
export function mergeSession(sessions: SessionInfo[], info: SessionInfo): SessionInfo[] {
  const idx = sessions.findIndex((x) => x.id === info.id);
  if (idx === -1) {
    return [...sessions, info];
  }
  const next = sessions.slice();
  next[idx] = info;
  return next;
}

function errMsg(err: unknown): string {
  // M8-WEB#37: util compartilhado (antes era String(err) cru).
  return friendlyError(err);
}

/**
 * M18.1.1 — bootId ja reconhecido neste dispositivo (localStorage). Persiste a dispensa do banner de
 * restauracao por BOOT, sobrevivendo a refresh/reabertura do PWA. Tolerante a ambientes sem
 * localStorage (SSR/teste/Safari privado).
 */
const SEEN_BOOT_KEY = 'td_restored_boot_id';

export function seenBootId(): string | null {
  try {
    return localStorage.getItem(SEEN_BOOT_KEY);
  } catch {
    return null;
  }
}

export function setSeenBootId(bootId: string): void {
  try {
    localStorage.setItem(SEEN_BOOT_KEY, bootId);
  } catch {
    /* sem localStorage: degrada para comportamento em-memoria desta sessao */
  }
}
